import { Rsf } from '../../../../../catalog/products/interface/phone';
import { SchemeBase } from '../../../../checkout/cart/scheme.class';
import { ProductContextBase } from '../../../../context/child/product-context.service';
import { RenewContextServiceBase } from '../../../../context/child/renew-context.service';
import { AttributeBase } from '../../interface/configurable';
import { EdpData, PhonePlanPrice } from '../../interface/context';
import { ProductBase } from '../../product';
import { EdpBase } from '../../subscription/edp';
import { ComplexBase } from '../complex';

/**
 * The Phone class
 * See {@link AttributeBase} to see how it is used
 */
export class PhoneBase extends ComplexBase {
    /**
     * The price with the current context
     */
    public price: number;

    /**
     * All plans prices
     */
    public plans_prices: PhonePlanPrice[];

    /**
     * @param plan - The plan
     * @return Returns the plan price based on the provided plan
     */
    public getPlanPrice(productContext: ProductContextBase): number {
        return this.selectPrice(
            productContext,
            this.context.contextService.get<RenewContextServiceBase>(RenewContextServiceBase),
        );
    }

    /**
     * Get EDP datas from a plan
     * @return AttributeBase
     */
    public getEdp(plan: string): EdpData {
        const edp = this.getEdpData();
        const phonePlanPrice = this.getPrice();
        const edpPrice = edp ? phonePlanPrice - edp.amount * edp.duration : null;
        if (!edp || edpPrice < 1) {
            return null;
        }
        return {
            price: this.selectPrice(
                this.context.create(plan, true),
                this.context.contextService.get<RenewContextServiceBase>(RenewContextServiceBase),
            ),
            duration: edp.duration,
            amount: edp.amount,
        };
    }

    public hasEdp(scheme: SchemeBase): boolean {
        return !!scheme.getProductByType(EdpBase);
    }

    public isEdpAppliable(scheme: SchemeBase, plan: string): boolean {
        return !this.hasEdp(scheme) && !!this.getEdp(plan);
    }

    /**
     * Get color structur
     * @return attributes color
     */
    public getColor(): AttributeBase {
        if (!this.getData('color_code')) {
            return null;
        }
        return {
            code: this.getData('color_code'),
            value: this.getData('color'),
        };
    }

    /**
     * Get capacity structur
     * @return attributes capacity
     */
    public getCapacity(): AttributeBase {
        if (!this.getData('capacity_code')) {
            return null;
        }
        return {
            code: this.getData('capacity_code'),
            value: this.getData('capacity'),
        };
    }

    /**
     * Remove EDP if we remove phone product in scheme
     * @param scheme
     * @returns
     */
    public updateSchemeOnRemove(scheme: SchemeBase): SchemeBase {
        const edpProduct: ProductBase = scheme.getProductByType(EdpBase);
        if (edpProduct) {
            scheme.remove(edpProduct.uniqueId);
        }
        delete scheme.productContext.edpData;
        scheme.updateContext(scheme.productContext);
        return super.updateSchemeOnRemove(scheme);
    }

    public updateSchemeOnAdd(scheme: SchemeBase): SchemeBase {
        scheme.productContext.edpData = this.getEdpData();
        scheme.updateContext(scheme.productContext);
        return scheme;
    }

    /**
     * to update the prices of phone : plan's prices, price
     */
    protected updatePrice(): void {
        const renewContext: RenewContextServiceBase =
            this.context.contextService.get<RenewContextServiceBase>(RenewContextServiceBase);
        const selectedPrice = this.selectPrice(this.context, renewContext);
        if (selectedPrice) {
            if (this.data.price !== this.oldPrice && this.specialPrice) {
                this.specialOldPrice = this.prices.sowo.base_price;
            }
            this.price = selectedPrice;
        }
        const rsf: Rsf = this.selectRsf(this.context, renewContext);
        let refund: number = this.price;
        let oldRefund: number = 0;
        let total: number = 0;
        if (!this.odr && !rsf) {
            return;
        } else {
            this.odrRsf = {};
        }
        if (this.odr) {
            refund = refund - this.odr.amount;
            oldRefund = this.oldPrice - this.odr.amount;
            oldRefund = oldRefund > 0 ? oldRefund : 1;
            total = Number(this.odr.amount);
            this.odrRsf.odr = this.odr;
        }
        if (rsf && !renewContext) {
            refund = refund - rsf.amount;
            total = rsf.amount + total;
            this.odrRsf.rsf = rsf;
        }
        if (refund < 0) {
            this.odrRsf.total = total + refund - 1;
        } else {
            this.odrRsf.total = total;
        }
        refund = refund > 0 ? refund : 1;

        this.odrRsf.refund = refund;
        this.odrRsf.oldRefund = oldRefund;
    }

    private selectPrice(
        productContextService: ProductContextBase,
        renewContextService: RenewContextServiceBase,
    ): number {
        if (!this.prices) {
            return 0;
        }
        let currentPrice: string;
        let plans: any;

        const plan = this.prices.plans[productContextService.plan];

        if (!this.prices.plans || !plan) {
            if (Number(productContextService.edp)) {
                return renewContextService?.edpPhone ? this.data.edp_phone.price : this.data.edp_price;
            } else {
                return Number(renewContextService ? this.data.price : this.data.final_price);
            }
        }

        if (renewContextService) {
            let clientType: string = renewContextService.getClientType();
            clientType = clientType === 'collaborator' && !!plan.renew.collaborator ? clientType : 'client';
            plans = plan.renew[clientType][renewContextService.type];
        } else {
            plans = plan.acquisition;
        }

        if (productContextService?.edp) {
            currentPrice = plans.edp_price || 1;
        } else {
            currentPrice = plans.price;
        }

        return Number.parseFloat(currentPrice);
    }

    private selectRsf(productContextService: ProductContextBase, renewContext: RenewContextServiceBase): Rsf {
        if (!this.prices || renewContext) {
            return null;
        }
        let currentRsf: Rsf = null;

        const plans: any = this.prices.plans[productContextService.plan]
            ? this.prices.plans[productContextService.plan].acquisition
            : null;
        if (plans && plans.rsfAmount) {
            currentRsf = {
                amount: plans.rsfAmount,
                file: plans.rsfPdf,
            };
        }

        return currentRsf;
    }
}
